// Client-side visibility gates for the global "My Schedule" and "Save a Seat"
// features. Release timestamps come from the Builder `global-data` model
// (enableMyScheduleAfter / enableSeatReservationsAfter, populated into the
// `scheduleControls` useState in app.vue).
//
// Why client-side: the SSR HTML is CDN-cached, so a server-resolved decision
// would be frozen at cache time (stale before/after a release). Instead the
// markup ships with the buttons HIDDEN and each browser reveals them once its
// own clock passes the release timestamp.
//
// A feature stays hidden when its timestamp is null/unset or still in the future.
import { isVipTicket } from '#shared/utilities/schedule/ticket';
import { normalizeSeatAllowlist, isSeatAllowlisted as matchSeatAllowlist } from '#shared/utilities/schedule/seat-allowlist';

export const useScheduleControls = () => {
    const { user } = useUserSession();
    const controls = useState('scheduleControls', () => ({
        enableMyScheduleAfter: null,
        // Seat reservations open in two waves. `enableSeatReservationsAfter` is
        // the VIP date — it keeps its name so the existing Builder records keep
        // working rather than needing a CMS migration — and
        // `enableSeatReservationsGeneralAfter` opens it to everyone else.
        enableSeatReservationsAfter: null,
        enableSeatReservationsGeneralAfter: null,
        // Emails that get the VIP wave regardless of what tier their ticket is —
        // staff, speakers, QA. It grants the EARLY date, not an exemption: an
        // allowlisted address still sees nothing until
        // enableSeatReservationsAfter has passed.
        alwaysEnableSeatReservationsFor: [],
    }));

    // false during SSR + first client render (matches cached HTML), flipped true
    // after mount so revealing the buttons can't cause a hydration mismatch.
    // No import.meta.client guard: onMounted never fires during SSR anyway, and
    // the guard is a Nuxt-only define that silently reads undefined under test —
    // which left clientReady stuck false and made gating tests pass while
    // proving nothing.
    const clientReady = useState('scheduleControlsReady', () => false);
    onMounted(() => { clientReady.value = true; });

    const isReleased = (ts) => {
        if (!ts) return false;
        const t = new Date(ts).getTime();
        return !Number.isNaN(t) && Date.now() >= t;
    };

    // In local dev, treat My Schedule as released (once client-ready) so its
    // button is testable without a Builder release timestamp. import.meta.dev is
    // stripped in the production build, so prod still honors the timestamp.
    //
    // Save a Seat has no such blanket dev force — it honors the real timestamp
    // everywhere — but setting enableSeatReservationsAfter in Builder to preview
    // it locally would reveal it in production at the same moment (dev and prod
    // read the same Production dataEnv). So: an OPT-IN local preview. Append
    // `?seats` to any URL in dev to reveal the seat buttons. Also gated on
    // import.meta.dev, which is stripped from the production build, so this
    // cannot ship — `?seats` does nothing on a deployed site.
    const route = useRoute();
    const previewSeats = computed(() => import.meta.dev && route.query.seats !== undefined);

    const showMySchedule = computed(() =>
        clientReady.value && (import.meta.dev || isReleased(controls.value.enableMyScheduleAfter))
    );
    // Seats open to VIP holders first, then to everyone. The viewer's tier comes
    // from the session cookie's ticketType, so this resolves client-side along
    // with the timestamps — same reason as above.
    //
    // Anyone we cannot identify as VIP — including a logged-OUT visitor, whose
    // tier we genuinely do not know yet — is treated as general admission. That
    // is the conservative direction: the early window stays invisible to
    // everyone except confirmed VIPs, rather than advertising an entitlement the
    // viewer may not have. A VIP who is logged out sees the buttons appear once
    // they sign in.
    const isVip = computed(() => isVipTicket(user.value?.ticketType));

    // Hand-maintained in Builder, so assume nothing about the shape: a list field
    // may arrive as bare strings or as objects wrapping the value, and a typed
    // list will carry stray case and whitespace. Normalise both sides — an
    // address that silently fails to match looks exactly like the feature being
    // broken for that person, with nothing on screen to explain it.
    // Matching lives in shared/ so the gate the visitor SEES and the gate that
    // DECIDES (reserveSeat) cannot drift apart. They shipped apart once already.
    const seatAllowlist = computed(() =>
        normalizeSeatAllowlist(controls.value.alwaysEnableSeatReservationsFor)
    );
    const isSeatAllowlisted = computed(() =>
        matchSeatAllowlist(user.value?.email, seatAllowlist.value)
    );

    // A VIP is released by EITHER date. The VIP date is the earlier one by
    // intent, but reading both means a record with only the general date filled
    // in — or the two entered the wrong way round — still lets VIPs in rather
    // than locking out the tier that is supposed to have early access.
    const seatsReleasedForViewer = computed(() => {
        const general = isReleased(controls.value.enableSeatReservationsGeneralAfter);
        // Allowlisted addresses ride the VIP wave. Kept separate from isVip
        // rather than folded into it, because isVip is the viewer's actual
        // ticket tier and gates other entitlements (the VIP Party); being on
        // this list means early SEAT access, not a VIP pass.
        if (!isVip.value && !isSeatAllowlisted.value) return general;
        return general || isReleased(controls.value.enableSeatReservationsAfter);
    });

    const showSeatReservations = computed(() =>
        clientReady.value && (previewSeats.value || seatsReleasedForViewer.value)
    );

    return { showMySchedule, showSeatReservations, isVip };
};
